import React, { useState } from 'react';
import {
    User,
    Mail,
    Bell,
    Shield,
    Palette,
    Globe,
    Key,
    Save,
    CheckCircle2,
    Settings
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/use-toast';

const SettingsManagement = () => {
    const { user } = useAuth();
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState('profile');
    const [isSaving, setIsSaving] = useState(false);

    const [settings, setSettings] = useState({
        name: user?.name || 'Admin',
        email: user?.email || '',
        siteName: 'HARA-XY',
        theme: 'dark',
        notifications: true,
        publicEmail: 'hello@hara-xy.com',
        publicPhone: '+91 7676 984958'
    });

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 800));
        setIsSaving(false);
        toast({
            title: "Settings Saved",
            description: "Your configurations have been updated successfully.",
            icon: <CheckCircle2 className="text-green-500" />
        });
    };

    const tabs = [
        { id: 'profile', label: 'My Profile', icon: User },
        { id: 'site', label: 'Site Settings', icon: Globe },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'branding', label: 'Branding', icon: Palette },
        { id: 'api', label: 'API & Integrations', icon: Key },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'profile':
                return (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <h2 className="text-xl font-bold text-silver">Profile Information</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm text-gray-400 font-mono uppercase tracking-wider">Display Name</label>
                                <input
                                    type="text"
                                    className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:border-silver outline-none transition-all"
                                    value={settings.name}
                                    onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm text-gray-400 font-mono uppercase tracking-wider">Email Address</label>
                                <input
                                    type="email"
                                    disabled
                                    className="w-full bg-black/20 border border-gray-800 rounded-xl px-4 py-3 text-gray-500 cursor-not-allowed"
                                    value={settings.email}
                                />
                            </div>
                        </div>
                        <div className="p-4 bg-silver/5 border border-silver/10 rounded-xl flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-silver to-gray-600 flex items-center justify-center text-2xl font-bold text-black font-mono">
                                {settings.name[0]}
                            </div>
                            <div>
                                <p className="text-silver font-medium">Avatar Image</p>
                                <button className="text-sm text-silver/60 hover:text-silver underline mt-1">Change photo</button>
                            </div>
                        </div>
                    </div>
                );
            case 'site':
                return (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <h2 className="text-xl font-bold text-silver">Website Configuration</h2>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm text-gray-400 font-mono uppercase tracking-wider">Site Name</label>
                                <input
                                    type="text"
                                    className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:border-silver outline-none transition-all"
                                    value={settings.siteName}
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-6 pt-4">
                                <div className="space-y-2">
                                    <label className="text-sm text-gray-400 font-mono uppercase tracking-wider">Public Company Email</label>
                                    <input
                                        type="email"
                                        className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:border-silver outline-none transition-all"
                                        value={settings.publicEmail}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm text-gray-400 font-mono uppercase tracking-wider">Public Company Phone</label>
                                    <input
                                        type="text"
                                        className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:border-silver outline-none transition-all"
                                        value={settings.publicPhone}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="py-20 text-center animate-in zoom-in-95 duration-300">
                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Settings className="text-gray-600" size={32} />
                        </div>
                        <h3 className="text-lg font-medium text-silver">Section under development</h3>
                        <p className="text-gray-500 max-w-xs mx-auto mt-2">We're working on making these configurations available for you.</p>
                    </div>
                );
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-silver mb-2">Settings</h1>
                <p className="text-gray-400">Manage your account and platform preferences</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 min-h-[600px]">
                {/* Navigation Sidebar */}
                <div className="w-full lg:w-64 space-y-1">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${activeTab === tab.id
                                        ? 'bg-silver text-black font-bold'
                                        : 'text-gray-400 hover:text-silver hover:bg-white/5'
                                    }`}
                            >
                                <Icon size={20} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Content Area */}
                <div className="flex-1 glass-morphism rounded-2xl p-8 border border-gray-800 overflow-hidden relative">
                    <form onSubmit={handleSave}>
                        {renderTabContent()}

                        <div className="mt-12 pt-8 border-t border-gray-800 flex justify-end">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="flex items-center gap-2 bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-silver transition-all disabled:opacity-50"
                            >
                                {isSaving ? (
                                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SettingsManagement;
